
import { RoleTrack, EngineerLevel, SkillNode, Comparison } from './types';

export const SKILL_MAPS: Record<RoleTrack, SkillNode[]> = {
  [RoleTrack.OPS]: [
    {
      level: EngineerLevel.ASSOCIATE,
      title: "Console & CLI Proficiency",
      description: "Managing EC2, S3, and VPC via the AWS Console and simple CLI commands. Focus on manual changes and basic troubleshooting.",
      tags: ["EC2", "S3", "IAM", "VPC"]
    },
    {
      level: EngineerLevel.CLOUD_ENGINEER,
      title: "IaC & CI/CD Automation",
      description: "Moving from manual to Terraform/CloudFormation. Implementing automated pipelines for infrastructure deployment.",
      tags: ["Terraform", "GitHub Actions", "CloudWatch", "ASG"]
    },
    {
      level: EngineerLevel.SENIOR_ENGINEER,
      title: "Platform Resilience & Cost Systems",
      description: "Architecting for multi-region failover. Implementing organization-wide cost governance and performance baseline systems.",
      tags: ["Multi-Account", "Service Quotas", "Cost Explorer API", "Route53 ARC"]
    },
    {
      level: EngineerLevel.PRINCIPAL_ENGINEER,
      title: "Strategic Infrastructure Governance",
      description: "Defining long-term technical strategy. Leading large-scale migrations and building hybrid-cloud connectivity frameworks.",
      tags: ["Direct Connect", "Control Tower", "Landing Zones", "AWS Organizations"]
    }
  ],
  [RoleTrack.PLATFORM]: [
    {
      level: EngineerLevel.ASSOCIATE,
      title: "Container Basics",
      description: "Building Docker images and running tasks on ECS/Fargate. Managing basic Kubernetes deployments.",
      tags: ["ECS", "Docker", "Fargate"]
    },
    {
      level: EngineerLevel.CLOUD_ENGINEER,
      title: "Developer Experience (DevEx)",
      description: "Building internal tooling and CLI wrappers to simplify AWS for app developers. Managing EKS clusters at scale.",
      tags: ["EKS", "Helm", "Internal Portals", "Service Catalog"]
    },
    {
      level: EngineerLevel.SENIOR_ENGINEER,
      title: "Infrastructure as Software",
      description: "Designing self-service platforms. Using CDK/Pulumi to create abstraction layers. Treating 'Infrastructure as a Product'.",
      tags: ["AWS CDK", "Custom Resources", "EventBridge", "Backstage"]
    },
    {
      level: EngineerLevel.PRINCIPAL_ENGINEER,
      title: "Cloud-Native Ecosystem Leadership",
      description: "Establishing org-wide standards for microservices. Implementing Service Mesh architectures and cross-account event buses.",
      tags: ["App Mesh", "Cloud Map", "Serverless Patterns", "ProtoBuf"]
    }
  ],
  [RoleTrack.SECURITY]: [
    {
      level: EngineerLevel.ASSOCIATE,
      title: "Identity & Access Management",
      description: "Creating IAM users, groups, and basic policies. Managing security group rules for instances.",
      tags: ["IAM", "Security Groups", "MFA"]
    },
    {
      level: EngineerLevel.CLOUD_ENGINEER,
      title: "Automated Compliance",
      description: "Implementing AWS Config rules and GuardDuty. Automating remediation of non-compliant resources.",
      tags: ["AWS Config", "GuardDuty", "Lambda Remediation"]
    },
    {
      level: EngineerLevel.SENIOR_ENGINEER,
      title: "Governance at Scale",
      description: "Defining Service Control Policies (SCPs) for AWS Organizations. Zero Trust architecture and cross-account security bus.",
      tags: ["SCPs", "Organizations", "Control Tower", "CloudTrail Lake"]
    },
    {
      level: EngineerLevel.PRINCIPAL_ENGINEER,
      title: "Enterprise Defense Strategy",
      description: "Architecting organization-wide threat detection and automated incident response systems. Designing Zero-Trust frameworks.",
      tags: ["Security Hub", "Detective", "KMS CMKs", "Shield Advanced"]
    }
  ],
  [RoleTrack.ARCHITECTURE]: [
    {
      level: EngineerLevel.ASSOCIATE,
      title: "The Well-Architected Baseline",
      description: "Understanding the 6 pillars. Learning how to map business requirements to specific AWS services without over-engineering.",
      tags: ["Well-Architected", "Cost Optimization", "S3 Classes"]
    },
    {
      level: EngineerLevel.SOLUTIONS_ARCHITECT,
      title: "Service Composition & Patterns",
      description: "Moving from single instances to distributed systems. Designing decoupling strategies using SQS, SNS, and Kinesis.",
      tags: ["SQS", "Event-Driven", "DynamoDB Modeling", "Lambda"]
    },
    {
      level: EngineerLevel.SENIOR_ARCHITECT,
      title: "Complex Systems Architecture",
      description: "Designing global, high-availability systems. Master of trade-offs: Latency vs. Consistency (CAP theorem) in AWS.",
      tags: ["Global Accelerator", "Aurora Global", "Active-Active", "WAF"]
    },
    {
      level: EngineerLevel.PRINCIPAL_ARCHITECT,
      title: "Enterprise Ecosystem Vision",
      description: "Defining the reference architecture for the entire company. Deciding when to build vs. buy. Managing the 'Architectural Registry'.",
      tags: ["Domain Driven Design", "Enterprise Service Bus", "Cloud Financial Ops"]
    }
  ]
};

export const COMPARISONS: Comparison[] = [
  {
    topic: "Problem Solving",
    junior: "Fixes the error message in the logs.",
    senior: "Determines why the system allowed the error to occur and adds preventative guardrails."
  },
  {
    topic: "Automation",
    junior: "Writes a script to perform a task.",
    senior: "Builds a system that detects the need for the task and executes it autonomously."
  },
  {
    topic: "Architecture",
    junior: "Follows a tutorial to connect services.",
    senior: "Evaluates trade-offs between managed vs self-hosted, latency vs cost, and consistency vs availability."
  },
  {
    topic: "Business Value",
    junior: "Focuses on 'Cool Tech' and certifications.",
    senior: "Focuses on reducing MTTR (Mean Time to Recovery) and improving deployment velocity."
  }
];
